/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import "./admin.scss";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import { Autocomplete, Input, TextField } from "@mui/material";
import "react-circular-progressbar/dist/styles.css";
import { BrandButton } from "../components/form/buttons";
import NetworkError from "../components/networkError";
import { getUserInfo, userLoggedIn } from "../components/user";
import { useNavigate } from "react-router-dom";
import {
  ExamProgressData,
  ExamRecord,
  UserInfoRecord,
} from "../components/types";

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  description?: string;
  structured_formatting?: StructuredFormatting;
  id: number;
  label: string;
  examId: string;
  examName: string;
}

interface SearchResult {
  email: string;
  id: number;
}

const Admin = () => {
  const [userData, setUserData] = useState<UserInfoRecord | null>();
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [selectedUser, setSelectedUser] = useState<PlaceType | null | any>(
    null
  );
  const [searchInput, setSearchInput] = useState<string>("");
  const [targetDataUser, setTargetDataUser] = useState<number | null>();
  const [sectionId, setSectionId] = useState();
  const [lastQuestion, setLastQuestion] = useState();
  const [time, setTime] = useState();
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const [doSearch, setDoSearch] = useState<boolean>(false);
  const [examOptions, setExamOptions] = useState<readonly PlaceType[]>([]);
  const [selectedExam, setSelectedExam] = useState<PlaceType | null | any>(
    null
  );
  const [examData, setExamData] = useState<ExamProgressData | null>();

  const { error, refetch } = useQuery(
    "examAdminResultsPageData",
    async () => {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/satapp-api/v1/getExamData",
        {
          token: userData?.token,
          exam_id: selectedExam.examId,
          userID: targetDataUser,
        }
      );

      if (!resp || !resp.data.success) {
        setHasError(true);
        return;
      }

      setDoSearch(false);
      setSectionId(resp.data.currentSectionId);
      setLastQuestion(resp.data.lastQuestionAnswered);
      setTime(resp.data.minutesRemaining);
      setExamData(resp.data);
    },
    { enabled: doSearch }
  );

  const udpateData = async () => {
    await axios.post(`${ApiEndpoint}wp-json/satapp-api/v1/updateUserExam`, {
      token: userData?.token,
      exam_id: selectedExam.examId,
      userData: {
        currentSectionId: sectionId,
        lastQuestionAnswered: lastQuestion,
        minutesRemaining: time,
      },
      userID: targetDataUser,
    });
    setSectionId("" as any);
    setLastQuestion("" as any);
    setTime("" as any);
  };

  const searchUsers = async () => {
    const resp = await axios.post(
      ApiEndpoint + "wp-json/satapp-api/v1/searchEmail",
      {
        token: userData?.token,
        s: searchInput,
      }
    );
    if (resp.data.length > 0) {
      const ops = resp?.data.map((item: SearchResult) => {
        return { label: item.email, id: item.id };
      });
      setOptions(ops);
    }
  };

  const getExamList = async () => {
    if (examOptions.length > 0) return;

    const resp = await axios.get(
      ApiEndpoint +
        "wp-json/satapp-api/v1/getExamNames?token=" +
        userData?.token
    );
    if (resp.data.exams.length > 0) {
      const list = resp.data.exams.map((item: ExamRecord) => {
        return {
          examId: item.examId,
          examName: item.examName,
          label: item.examName,
        };
      });
      setExamOptions(list);
    }
  };

  useEffect(() => {
    if (!doSearch) return;
    refetch();
  }, [doSearch]);

  useEffect(() => {
    if (!selectedExam) {
      return;
    } else {
      setDoSearch(true);
    }
  }, [selectedExam]);

  useEffect(() => {
    if (selectedUser) {
      setTargetDataUser(selectedUser?.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    setSectionId("" as any);
    setLastQuestion("" as any);
    setTime("" as any);
    if (searchInput && searchInput.length > 3) {
      searchUsers();
    }
  }, [searchInput]);

  useEffect(() => {
    if (userData) {
      getExamList();
    }
  }, [userData]);

  useEffect(() => {
    if (!userLoggedIn()) {
      navigate("/");
    } else {
      setUserData(getUserInfo());
    }
  }, []);

  const stampToTime = (): string => {
    if (time) {
      const minutes: number = Math.floor(time / 60 / 1000) as number;
      const seconds: number = Math.floor((time / 1000) % 60) as number;

      return minutes + "m " + seconds + "s";
    } else {
      return "";
    }
  };

  if (error || (!userData?.isadmin && !userData?.istutor)) return <LoadError />;

  return (
    <div className="adminPage">
      <div className="header"></div>

      <div className="headline">
        <h1>User Admin Dashboard</h1>
      </div>
      <div className="pageContent results">
        <div style={{ margin: "40px" }}>
          {userData.isadmin ||
            (userData.istutor && (
              <div className="userSwitcher">
                <Autocomplete
                  disablePortal
                  options={options}
                  value={selectedUser}
                  sx={{ width: 300 }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField {...params} label="choose user" />
                  )}
                  onChange={(event: any, newValue: any) => {
                    setOptions(newValue ? [newValue, ...options] : options);
                    setSelectedUser(newValue);
                  }}
                  onInputChange={(event: any, newInputValue: any) => {
                    setSearchInput(newInputValue);
                  }}
                  noOptionsText="No Users"
                />
                <Autocomplete
                  disablePortal
                  options={examOptions}
                  value={selectedExam}
                  sx={{ width: 300 }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField {...params} label="choose exam" />
                  )}
                  onChange={(event: any, newValue: any) => {
                    // setExamOptions(
                    //   newValue ? [newValue, ...examOptions] : examOptions
                    // );
                    setSelectedExam(newValue);
                  }}
                  noOptionsText="No exam chosen"
                  getOptionLabel={(option: PlaceType) => option.label}
                  disabled={!selectedUser}
                />
              </div>
            ))}
        </div>

        {examData?.examComplete && (
          <>
            <div className="resultsHolder">
              <BrandButton
                onClick={() =>
                  navigate("/exam-results/", {
                    state: {
                      exam_id: selectedExam.examId,
                      userID: targetDataUser,
                      passedExamData: examData,
                    },
                  })
                }
              >
                View Results For This Exam
              </BrandButton>
            </div>
            <hr />
          </>
        )}

        <div
          className=""
          style={{
            margin: "30px",
            display: selectedExam && selectedUser ? "flex" : "none",
            gap: "15px",
          }}
        >
          <div style={{ minWidth: "100px" }}>
            <strong>user ID:</strong> <span>{targetDataUser}</span>
          </div>
          <div>
            <strong>Current Section ID:</strong>{" "}
            <Input
              value={sectionId}
              onChange={(evt) => setSectionId(evt.target.value as any)}
            />
            <p>Please note: sections #'s start at 0!</p>
          </div>
          <div>
            <strong>Last Question Answered:</strong>{" "}
            <Input
              value={lastQuestion}
              onChange={(evt) => setLastQuestion(evt.target.value as any)}
            />
            <p>This is the WP question ID.</p>
          </div>
          <div>
            <strong>Time Remaining:</strong>{" "}
            <Input
              value={time}
              onChange={(evt) => setTime(evt.target.value as any)}
            />
            <br />
            <p>{stampToTime()}</p>
          </div>
          <div style={{ minWidth: "120px" }}>
            <BrandButton onClick={() => udpateData()}>Update Data</BrandButton>
          </div>
        </div>
        <hr />
      </div>
      <NetworkError open={hasError} />
    </div>
  );
};
export default Admin;
